import React from "react";
import type Highcharts from "highcharts";
import { getMainAxisSelectedPoints } from "@ui/Highcharts/plugins/stickyTooltip";
import { renderToString } from "react-dom/server";
import { FLAG_AXIS } from "../../../../../../../../Chart/series/getAnalysisFlagSerie";
import validateInterface from "@ui/lib/validateInterface";
import { GAP_POINT_METADATA, PointMetadata } from "../../../../../../../../Chart/PointMetadata";
import type { Options } from "@ui/Highcharts";
import useYMinMax from "@ui/Highcharts/hooks/useYMinMax";
import useAnalysisXAxis from "../../../../../../../hooks/useAnalysisXAxis";
import type { SelectedQuantity } from "../../../../../../../../useSelectedQuantities";
import AxisTitle, { colorClassName } from "../../../../../../../../Chart/AxisTitle";
import { useQuantityNoDataIntervals } from "../../../../../../../../useNoDataIntervals";
import t from "../../../../../../../../../../i18n";
import useNoDataPlotBands from "@ui/Highcharts/hooks/useNoDataPlotBands";
import type { ActiveAnalysis } from "../../../../../../../../../../api/useActiveAnalysesStream";
import type { Source } from "../../../../../../../../../../api/useSources";
import type { Project } from "../../../../../../../../../../api/useProjects";
import useGroupBreaks from "../../../../../../../../Chart/hooks/useGroupBreaks";
import { AnalysisDateChartTooltip } from "../../../../../../../../Chart/ChartTooltip/common/AnalysisDateChartTooltip";
import { FlagChartTooltip } from "../../../../../../../../Chart/ChartTooltip/common/FlagChartTooltip";
import Text from "@ui/Text";
import { useQuantityByName } from "../../../../../../../../../../api/useQuantities";
import formatNumber from "@ui/i18n/formatNumber";
import { QuantityChartTooltip } from "../../../../../../../../Chart/ChartTooltip/data_symptom/QuantityChartTooltip";
import { SymptomQuantityChartTooltip } from "../../../../../../../../Chart/ChartTooltip/data_symptom/SymptomQuantityChartTooltip";
import { ChartTooltip } from "../../../../../../../../Chart/ChartTooltip";
import type { Event } from "../../../../../../../../../../api/useEvents";
import { CHART_SELECTION_PLOT_LINE_MARGIN } from "../../../../../../../../Chart/hooks/useAnalysisSelectionPlotLines";

export const NORMALIZED_GAP_Y_AXIS = "NORMALIZED_GAP_Y_AXIS";

const useOptions = ({
  chartRef,
  series,
  analyses,
  selectedQuantities,
  project,
  source,
  timeZone,
  event,
}: {
  chartRef: React.RefObject<Highcharts.Chart>;
  series: (
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesArearangeOptions
    | Highcharts.SeriesScatterOptions
  )[];
  analyses: ActiveAnalysis[];
  selectedQuantities: SelectedQuantity[];
  project: Project;
  source: Source;
  timeZone: string;
  event: Event;
}): Options => {
  const quantityByName = useQuantityByName(project.id);
  const xBreaks = useGroupBreaks(project.id, analyses);
  const initialXAxisOptions = useAnalysisXAxis({
    chartRef,
    project,
    source,
    analyses,
    boundaries: "active-analyses",
    event,
  });
  const initialYAxisOptions = useYMinMax({
    chartRef,
    yAxisId: NORMALIZED_GAP_Y_AXIS,
  });

  useNoDataPlotBands({
    chartRef,
    intervals: useQuantityNoDataIntervals(
      analyses,
      selectedQuantities,
      "normalizedGap",
      project.id,
    ),
  });

  const constantTooltipFormatter = React.useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      const selectedPoints = getMainAxisSelectedPoints(this.series.chart);
      const selectedCount = this.point.selected ? selectedPoints.length : 0;

      const metadata = validateInterface(PointMetadata, this.point.options.custom);
      const humanValueOptions = metadata.type === GAP_POINT_METADATA ? metadata.human : null;

      const quantity = quantityByName[this.series.name];
      const hasQuantityField = quantity !== undefined;

      const option = {
        label: t("symptoms.chart.tooltip.selected_chart.gap"),
        value: humanValueOptions
          ? formatNumber(humanValueOptions.value, {
              sign: humanValueOptions.sign ?? undefined,
              precision: humanValueOptions.precision ?? undefined,
              unit: humanValueOptions.unit ?? undefined,
            })
          : "-",
      };

      const isSymptom =
        metadata.type === GAP_POINT_METADATA ? (metadata.isSymptom ?? false) : false;
      const descriptionSymptom = this.point.description ?? null;

      return renderToString(
        <ChartTooltip>
          {selectedCount > 1 ? (
            <Text type="h4">
              {t("measures.chart.tooltip.selectedPoints", {
                count: selectedPoints.length,
              })}
            </Text>
          ) : (
            <>
              <AnalysisDateChartTooltip
                date={this.point.x}
                group={metadata.group}
                label={metadata.label}
                timezone={timeZone}
              />
              {hasQuantityField ? (
                <QuantityChartTooltip
                  name={quantity.name}
                  description={quantity.description}
                  option={option}
                />
              ) : null}
              {descriptionSymptom ? (
                <SymptomQuantityChartTooltip
                  description={descriptionSymptom}
                  isSymptom={isSymptom}
                />
              ) : null}
            </>
          )}
          {"flags" in metadata ? <FlagChartTooltip flags={metadata.flags} /> : null}
        </ChartTooltip>,
      );
    },
    [quantityByName, timeZone],
  );

  return React.useMemo(() => {
    const options: Options = {
      initial: {
        xAxis: initialXAxisOptions,
        yAxis: [initialYAxisOptions],
      },
      chart: {
        zoomType: "xy",
        marginTop: CHART_SELECTION_PLOT_LINE_MARGIN,
      },
      xAxis: {
        breaks: xBreaks,
      },
      yAxis: [
        {
          id: NORMALIZED_GAP_Y_AXIS,
          title: {
            /**
             * @remarks
             * Here, we manage the dynamic display of the axis title
             * The same problem is encountered on the dashboard side, in order to maintain a multi-quantity title.
             * If you modify this part, remember to check consistency with the dashboard code.
             */
            text: selectedQuantities.length
              ? renderToString(
                  <>
                    {t("symptom.chart.header.option.normalizedGap")}
                    {selectedQuantities.map((q, index) => (
                      <>
                        {index === 0 && <> - </>}
                        <AxisTitle key={q.quantity.name} className={colorClassName(q.colorIndex)}>
                          {q.quantity.name}
                        </AxisTitle>
                        {index < selectedQuantities.length - 1 && <>,</>}
                      </>
                    ))}
                  </>,
                )
              : undefined,
            useHTML: true,
          },
          labels: {
            sign: true,
            unit: "σ",
            noSpaceBetweenValueAndUnit: true,
          },
        },
        FLAG_AXIS,
      ],
      tooltip: {
        formatter: constantTooltipFormatter,
      },
      navigator: {
        enabled: true,
        xAxis: {
          breaks: xBreaks,
          max: Date.now(),
        },
      },
      series,
    };
    return options;
  }, [
    initialXAxisOptions,
    initialYAxisOptions,
    xBreaks,
    selectedQuantities,
    constantTooltipFormatter,
    series,
  ]);
};
export default useOptions;
